import React, { useMemo, useEffect, useState } from 'react'
import { Box, Paper, Typography, Breadcrumbs, Link as MuiLink, CircularProgress, Divider, makeStyles, Tooltip } from '@material-ui/core'
import { Link, useParams, generatePath, useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation, queryCache } from 'react-query'
import { useSnackbar } from 'notistack'
import Button from 'UI/Button/ButtonWithPreloader'
import ModalComponent from 'UI/Modal'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import RichContent from 'UI/RichContent'
import { usePermission } from 'hooks/usePermission'
import { KB_MANAGE_PERM } from 'constants/permissions'
import { KB_LIST_URL, KB_EDIT_URL, KB_VIEW_URL } from 'constants/routes'
import { getArticle, deleteArticle } from 'services/knowledgeBase'
import { buildArticleContent } from 'utils/knowledgeBaseContent'
import { convertDateWithTime } from 'utils/formatters'
import { SOMETHING_WENT_WRONG } from 'constants/errors'

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    color: theme.palette.text.secondary,
  },
  toc: {
    padding: '1rem 1.25rem',
    borderRadius: '6px',
    backgroundColor: theme.palette.action.hover,
  },
  tocRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.15rem 0',
  },
  tocLink: {
    color: theme.palette.text.primary,
  },
  copyLink: {
    marginLeft: '0.6rem',
    fontSize: '0.75rem',
    color: theme.palette.text.disabled,
    cursor: 'pointer',
  },
  body: {
    marginTop: '1.5rem',
  },
  versionHistory: {
    color: theme.palette.text.secondary,
  },
  deleteButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
}))

const KnowledgeBaseView = () => {
  const s = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { id } = useParams<{ id: string }>()
  const articleId = Number(id)
  const { hasPermission } = usePermission()
  const canManage = hasPermission(KB_MANAGE_PERM)

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const { data, isLoading, isError } = useQuery(['kb-article', articleId], () => getArticle(articleId))

  const [deleteArticleMut, { isLoading: isDeleting }] = useMutation(deleteArticle, {
    onSuccess: () => {
      queryCache.invalidateQueries('kb-articles')
      setDeleteModalOpen(false)
      enqueueSnackbar('Page successfully deleted')
      history.push(KB_LIST_URL)
    },
    onError: () => enqueueSnackbar(SOMETHING_WENT_WRONG),
  })

  const content = useMemo(() => buildArticleContent(data ? data.data.bodyHtml : ''), [data])

  useEffect(() => {
    if (!data) return
    const anchor = decodeURIComponent(location.hash.replace('#', ''))
    if (!anchor) return
    const element = document.getElementById(anchor)
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [data, content, location.hash])

  const copySectionLink = (anchorId: string) => {
    const link = `${window.location.origin}${generatePath(KB_VIEW_URL, { id: articleId })}#${anchorId}`
    if (navigator.clipboard) navigator.clipboard.writeText(link)
    enqueueSnackbar('Section link copied — paste it into a page link')
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p="4rem">
        <CircularProgress color="primary" />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box component={Paper} p="3rem" mb="2rem" display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h3" color="textSecondary">
          This page could not be loaded. It may have been deleted or is temporarily unavailable.
        </Typography>
        <Box mt="2rem">
          <Button color="primary" variant="contained" onClick={() => history.push(KB_LIST_URL)}>
            Back to Knowledge Base
          </Button>
        </Box>
      </Box>
    )
  }

  const article = data.data

  return (
    <Box component={Paper} p="3rem" mb="2rem">
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Breadcrumbs separator=">" className={s.breadcrumb}>
          <MuiLink component={Link} to={KB_LIST_URL} color="inherit">
            Knowledge Base
          </MuiLink>
          <Typography color="textSecondary">{article.sectionName}</Typography>
          <Typography color="textPrimary">{article.title}</Typography>
        </Breadcrumbs>
        {canManage && (
          <Box display="flex" alignItems="center" style={{ gap: '0.75rem' }}>
            <Button color="primary" variant="outlined" onClick={() => history.push(generatePath(KB_EDIT_URL, { id: articleId }))}>
              Edit
            </Button>
            <Button variant="outlined" className={s.deleteButton} onClick={() => setDeleteModalOpen(true)}>
              Delete
            </Button>
          </Box>
        )}
      </Box>

      <Box mt="1.5rem">
        <Typography variant="h1" color="textSecondary">
          {article.title}
        </Typography>
      </Box>

      {content.toc.length > 0 && (
        <Box mt="1.5rem" className={s.toc}>
          <Typography variant="h4" color="textSecondary">
            Contents
          </Typography>
          <Box mt="0.5rem">
            {content.toc.map((entry) => (
              <Box key={entry.id} className={s.tocRow} style={{ paddingLeft: `${(entry.level - 1) * 1}rem` }}>
                <MuiLink href={`#${entry.id}`} className={s.tocLink}>
                  {entry.text}
                </MuiLink>
                <Tooltip title="Copy a link to this section">
                  <MuiLink component="button" type="button" onClick={() => copySectionLink(entry.id)} className={s.copyLink}>
                    Copy link
                  </MuiLink>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <RichContent html={article.bodyHtml} className={s.body} />

      <Box mt="2.5rem">
        <Divider />
        <Box mt="1.5rem">
          <Typography variant="h4" color="textSecondary">
            Version History
          </Typography>
          <Box mt="0.5rem" className={s.versionHistory}>
            <Typography variant="body2">Last Updated By: {article.updatedBy || 'n/a'}</Typography>
            <Typography variant="body2">Last Updated on: {convertDateWithTime(article.updatedDate)}</Typography>
          </Box>
        </Box>
      </Box>

      <ModalComponent open={deleteModalOpen}>
        <ConfirmationContent
          isLoading={isDeleting}
          renderedElement={<span>{`Are you sure you want to delete ${article.title}?`}</span>}
          handleCancel={() => setDeleteModalOpen(false)}
          handleConfirm={() => deleteArticleMut(articleId)}
        />
      </ModalComponent>
    </Box>
  )
}

export default KnowledgeBaseView
